/* ---------------------------------------------------------------------------
   Load guard.

   Every section that uses the rail emits its own script_tag for this file, so
   a page with three rail sections executes it three times. Shopify does not
   dedupe script_tag, and nothing here was idempotent: each pass bound another
   full set of listeners to every rail already on the page, so one arrow click
   fired three handlers and the track jumped three items instead of one.

   First execution wins; later ones return immediately.
   --------------------------------------------------------------------------- */
if (!window.__hpRailLoaded) {
  window.__hpRailLoaded = true;
/* ---------------------------------------------------------------------------
   HpRail - shared horizontal rail
   Native scroll-snap plus a desktop-only pointer drag layer, horizontal wheel
   handling, flick-aware settling and optional prev/next arrows.
   Used by Feature Reel; Shop Perks carries its own copy of the same logic and
   can be migrated onto this module.

   Markup contract:
     [data-hp-rail]            wrapper
       [data-hp-rail-track]    the scroll container
         [data-hp-rail-item]   each slide
       [data-hp-rail-prev]     optional button
       [data-hp-rail-next]     optional button
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var FLICK = 0.35;
  var MOVED = 6;
  var SNAP_BACK = 500;

  function HpRail(root) {
    this.root = root;
    this.track = root.querySelector('[data-hp-rail-track]');
    if (!this.track) return;
    this.items = [].slice.call(root.querySelectorAll('[data-hp-rail-item]'));
    this.prevBtn = root.querySelector('[data-hp-rail-prev]');
    this.nextBtn = root.querySelector('[data-hp-rail-next]');

    this.dragEnabled = root.getAttribute('data-rail-drag') !== 'false';
    this.wheelEnabled = root.getAttribute('data-rail-wheel') !== 'false';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (this.dragEnabled && this.fine) this.bindDrag();
    if (this.wheelEnabled) this.bindWheel();
    this.bindArrows();
    this.bindScroll();
    this.syncArrows();
  }

  HpRail.prototype.behavior = function () {
    return this.reduced ? 'auto' : 'smooth';
  };

  HpRail.prototype.offsetOf = function (el) {
    return el.offsetLeft - this.track.offsetLeft;
  };

  HpRail.prototype.nearestIndex = function () {
    var x = this.track.scrollLeft;
    var best = 0;
    var bestD = Infinity;
    for (var i = 0; i < this.items.length; i++) {
      var d = Math.abs(this.offsetOf(this.items[i]) - x);
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  };

  HpRail.prototype.goTo = function (i) {
    if (i < 0) i = 0;
    if (i > this.items.length - 1) i = this.items.length - 1;
    var target = this.items[i];
    if (!target) return;
    this.track.classList.add('is-settling');
    this.track.scrollTo({ left: this.offsetOf(target), behavior: this.behavior() });
    var self = this;
    window.setTimeout(function () { self.track.classList.remove('is-settling'); self.syncArrows(); }, SNAP_BACK);
  };

  HpRail.prototype.step = function (dir) {
    this.goTo(this.nearestIndex() + dir);
  };

  /* ---- arrows ----------------------------------------------------------- */
  HpRail.prototype.bindArrows = function () {
    var self = this;
    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.step(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.step(1); });
  };

  HpRail.prototype.syncArrows = function () {
    var t = this.track;
    // lets a section hide its arrows entirely when everything already fits
    this.root.classList.toggle('has-overflow', t.scrollWidth > t.clientWidth + 2);
    var atStart = t.scrollLeft <= 2;
    var atEnd = t.scrollLeft >= t.scrollWidth - t.clientWidth - 2;
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  };

  HpRail.prototype.bindScroll = function () {
    var self = this;
    var t = null;
    this.track.addEventListener('scroll', function () {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(function () { self.syncArrows(); }, 80);
    }, { passive: true });
    window.addEventListener('resize', function () { self.syncArrows(); });
  };

  /* ---- pointer drag ----------------------------------------------------- */
  HpRail.prototype.bindDrag = function () {
    var self = this;
    var track = this.track;
    var down = false, dragging = false;
    var startX = 0, startScroll = 0, lastX = 0, lastT = 0, velocity = 0;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true; dragging = false;
      startX = e.clientX; startScroll = track.scrollLeft;
      lastX = e.clientX; lastT = e.timeStamp; velocity = 0;
    });

    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < MOVED) return;
        dragging = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      }
      e.preventDefault();
      track.scrollLeft = startScroll - dx;
      var dt = e.timeStamp - lastT;
      if (dt > 0) velocity = (e.clientX - lastX) / dt;
      lastX = e.clientX; lastT = e.timeStamp;
    });

    function release(e) {
      if (!down) return;
      down = false;
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      track.classList.add('is-settling');
      try { track.releasePointerCapture(e.pointerId); } catch (err) {}
      var i = self.nearestIndex();
      if (velocity < -FLICK) i += 1;
      else if (velocity > FLICK) i -= 1;
      self.justDragged = true;
      window.setTimeout(function () { self.justDragged = false; }, 60);
      self.goTo(i);
    }
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    track.addEventListener('click', function (e) {
      if (track.classList.contains('is-dragging') || self.justDragged) {
        e.preventDefault(); e.stopPropagation();
      }
    }, true);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
  };

  /* ---- horizontal wheel ------------------------------------------------- */
  HpRail.prototype.bindWheel = function () {
    var self = this;
    var timer = null;
    this.track.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      self.track.classList.add('is-settling');
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(function () { self.goTo(self.nearestIndex()); }, 140);
    }, { passive: true });
  };

  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('[data-hp-rail]');
    [].slice.call(nodes).forEach(function (el) {
      if (el.hpRail) return;
      el.hpRail = new HpRail(el);
    });
  }

  window.HpRail = HpRail;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else { boot(); }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();




  /* -------------------------------------------------------------------------
     Arrow target correction.

     The step calculation resolves to an item's offset plus the track's leading
     inset, so on a rail with a bleed inset every arrow click overshoots by that
     inset: the cards never settle on a snap position, and the final click asks
     for a target past the end of the scroll range.

     Rather than guess at the caller, snap any programmatic scroll to the nearest
     real item offset and clamp it to the scrollable range. Drag and wheel are
     untouched - they set scrollLeft directly and were always correct.
     ------------------------------------------------------------------------- */
  function hpRailSnapFix(root) {
    var tracks = (root || document).querySelectorAll('[data-hp-rail-track]');
    Array.prototype.forEach.call(tracks, function (track) {
      if (track.__hpSnapFixed) return;
      track.__hpSnapFixed = true;

      var native = track.scrollTo.bind(track);
      track.scrollTo = function (opts) {
        if (!opts || typeof opts.left !== 'number') return native(opts);
        var items = track.querySelectorAll('[data-hp-rail-item]');
        if (!items.length) return native(opts);

        var max = Math.max(0, track.scrollWidth - track.clientWidth);
        var best = null;
        var bestDistance = Infinity;

        for (var i = 0; i < items.length; i++) {
          var offset = items[i].offsetLeft;
          var distance = Math.abs(offset - opts.left);
          if (distance < bestDistance) { bestDistance = distance; best = offset; }
        }

        if (best === null) best = opts.left;
        best = Math.max(0, Math.min(best, max));

        return native({ left: best, behavior: opts.behavior || 'smooth' });
      };
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { hpRailSnapFix(); });
  } else {
    hpRailSnapFix();
  }

  document.addEventListener('shopify:section:load', function (e) {
    hpRailSnapFix(e && e.target ? e.target : document);
  });

}
